import { Node, mergeAttributes } from '@tiptap/core';
import { Node as ProseMirrorNode } from 'prosemirror-model';
import { TextSelection } from 'prosemirror-state';

const createCollapsibleId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `col-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

// Shift-Enter / Mod-Enter (Cmd-Enter on Mac, Ctrl-Enter on Windows/Linux via
// TipTap's 'Mod' alias): exit the Collapsible, dropping the cursor into a
// new paragraph placed right after the whole section.
const exitCollapsibleSection = (editor: any): boolean => {
  const { $from, empty } = editor.state.selection;
  if (!empty) {
    return false;
  }

  for (let d = $from.depth; d > 0; d--) {
    if ($from.node(d).type.name !== 'collapsibleSection') {
      continue;
    }

    const sectionEnd = $from.after(d);
    const paragraphType = editor.schema.nodes['paragraph'];
    if (!paragraphType) {
      return false;
    }

    return editor.commands.command(({ tr, dispatch }: { tr: any; dispatch: any }) => {
      if (dispatch) {
        tr.insert(sectionEnd, paragraphType.createAndFill());
        tr.setSelection(TextSelection.near(tr.doc.resolve(sectionEnd + 1), 1));
        dispatch(tr.scrollIntoView());
      }
      return true;
    });
  }

  return false;
};

export interface CollapsibleSectionOptions {
  HTMLAttributes: Record<string, unknown>;
  onOpenMenu?: (sectionPos: number, triggerElement: HTMLElement) => void;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    collapsibleSection: {
      /**
       * Insert a collapsible section
       */
      insertCollapsibleSection: () => ReturnType;
      /**
       * Toggle the collapsible section
       */
      toggleCollapsibleSection: () => ReturnType;
    };
  }
}

export const CollapsibleSection = Node.create<CollapsibleSectionOptions>({
  name: 'collapsibleSection',

  group: 'block',

  content: 'collapsibleTitle collapsibleContent',

  defining: true,

  isolating: false,

  allowGapCursor: true,

  selectable: true,

  draggable: false,

  addOptions() {
    return {
      HTMLAttributes: {},
      onOpenMenu: undefined,
    };
  },

  addAttributes() {
    return {
      collapsibleId: {
        default: null,
        parseHTML: element => element.getAttribute('data-collapsible-id'),
        renderHTML: attributes => {
          const id = attributes['collapsibleId'];
          return id ? { 'data-collapsible-id': id } : {};
        },
      },
      open: {
        default: false,
        parseHTML: element => element.getAttribute('data-open') === 'true',
        renderHTML: attributes => ({
          'data-open': attributes['open'] ? 'true' : 'false',
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="collapsible"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(
        {
          'data-type': 'collapsible',
          class: 'collapsible-section',
        },
        this.options.HTMLAttributes,
        HTMLAttributes
      ),
      0,
    ];
  },

  addNodeView() {
    return ({ node, getPos, editor }) => {
      let currentNode = node;
      const dom = document.createElement('div');
      dom.className = 'collapsible-section';
      dom.setAttribute('data-type', 'collapsible');
      dom.setAttribute('data-open', node.attrs['open'] ? 'true' : 'false');
      if (node.attrs['collapsibleId']) {
        dom.setAttribute('data-collapsible-id', String(node.attrs['collapsibleId']));
      }
      dom.style.position = 'relative';
      
      // Minimal styling - let CSS handle most of it
      if (editor.isEditable) {
        dom.style.cssText = 'position: relative; border: 2px solid var(--primary-color, #3b82f6); background-color: #f3f4f6; border-radius: 0.375rem; margin: 0.5rem 0; padding: 0;';
      } else {
        dom.style.cssText = 'position: relative; border: 1px solid #e5e7eb; background-color: transparent; border-radius: 0.375rem; margin: 0.5rem 0; padding: 0;';
      }

      const contentDOM = document.createElement('div');
      contentDOM.style.cssText = 'display: block;';

      dom.appendChild(contentDOM);

      let menuButton: HTMLButtonElement | null = null;
      menuButton = document.createElement('button');
        menuButton.className = 'collapsible-menu-btn';
        menuButton.type = 'button';
        menuButton.title = 'Section actions';
        menuButton.setAttribute('aria-label', 'Section actions');
        menuButton.innerHTML = '<span class="material-icons" style="font-size: 16px;">more_vert</span>';
        menuButton.style.cssText = `
          position: absolute;
          top: 0.3rem;
          right: 0.5rem;
          width: 1.9rem;
          height: 1.9rem;
          padding: 0;
          background-color: #f3f4f6;
          border: 1px solid #d1d5db;
          border-radius: 0.375rem;
          color: #6b7280;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          z-index: 10;
          font-family: inherit;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
        `;

        menuButton.addEventListener('mouseenter', () => {
          if (!menuButton) {
            return;
          }
          menuButton.style.backgroundColor = '#e5e7eb';
          menuButton.style.borderColor = '#9ca3af';
          menuButton.style.color = '#4b5563';
          menuButton.style.boxShadow = '0 2px 6px rgba(15, 23, 42, 0.12)';
        });

        menuButton.addEventListener('mouseleave', () => {
          if (!menuButton) {
            return;
          }
          menuButton.style.backgroundColor = '#f3f4f6';
          menuButton.style.borderColor = '#d1d5db';
          menuButton.style.color = '#6b7280';
          menuButton.style.boxShadow = '0 1px 2px rgba(15, 23, 42, 0.08)';
        });

        const openMenu = (event: Event) => {
          event.preventDefault();
          event.stopPropagation();
          if (typeof getPos !== 'function') {
            return;
          }

          const sectionPos = getPos();
          if (typeof sectionPos !== 'number' || !menuButton) {
            return;
          }

          this.options.onOpenMenu?.(sectionPos, menuButton);
        };

        menuButton.addEventListener('mousedown', openMenu, { capture: true });
        menuButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
        }, { capture: true });
        menuButton.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            openMenu(event);
          }
        });

      dom.appendChild(menuButton);

      let nestedToggleButton: HTMLButtonElement | null = null;


      const getNestedInfo = (sectionNode: ProseMirrorNode) => {
        let nestedCount = 0;
        let allNestedOpen = true;

        sectionNode.descendants((child) => {
          if (child.type.name === 'collapsibleSection') {
            nestedCount += 1;
            if (!child.attrs['open']) {
              allNestedOpen = false;
            }
          }
        });

        return {
          hasNested: nestedCount > 0,
          allNestedOpen,
        };
      };

      const toggleNestedSections = () => {
        if (typeof getPos !== 'function') {
          return;
        }

        const sectionPos = getPos();
        if (typeof sectionPos !== 'number') {
          return;
        }

        const { allNestedOpen } = getNestedInfo(currentNode);
        const targetOpen = !allNestedOpen;
        const tr = editor.state.tr;
        tr.setMeta('isCollapsibleToggle', true);
        tr.setMeta('toggleSource', 'CollapsibleSection nested toggle');

        let modified = false;
        currentNode.descendants((child, relativePos) => {
          if (child.type.name !== 'collapsibleSection') {
            return;
          }

          const absolutePos = sectionPos + 1 + relativePos;
          tr.setNodeMarkup(absolutePos, undefined, {
            ...child.attrs,
            open: targetOpen,
          });
          modified = true;
        });

        if (modified) {
          editor.view.dispatch(tr);
        }
      };

      const syncNestedToggleButton = () => {
        const titleElement = dom.querySelector('[data-type="collapsible-title"]') as HTMLElement | null;
        if (!titleElement) {
          return;
        }

        const { hasNested, allNestedOpen } = getNestedInfo(currentNode);
        if (!hasNested) {
          if (nestedToggleButton) {
            nestedToggleButton.remove();
            nestedToggleButton = null;
          }
          titleElement.style.paddingRight = '1rem';
          return;
        }

        if (!nestedToggleButton) {
          nestedToggleButton = document.createElement('button');
          nestedToggleButton.type = 'button';
          nestedToggleButton.title = 'Toggle nested sections';
          nestedToggleButton.innerHTML = '<span class="material-icons" style="font-size: 16px;">unfold_more</span>';
          nestedToggleButton.style.cssText = `
            position: absolute;
            top: 0.35rem;
            right: 3rem;
            width: 1.75rem;
            height: 1.75rem;
            border: 1px solid #d1d5db;
            border-radius: 0.375rem;
            background: #f3f4f6;
            color: #6b7280;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            z-index: 11;
            padding: 0;
            transition: all 0.15s ease;
          `;

          nestedToggleButton.addEventListener('mouseenter', () => {
            if (!nestedToggleButton) {
              return;
            }
            nestedToggleButton.style.background = '#e5e7eb';
            nestedToggleButton.style.borderColor = '#9ca3af';
            nestedToggleButton.style.color = '#4b5563';
          });

          nestedToggleButton.addEventListener('mouseleave', () => {
            if (!nestedToggleButton) {
              return;
            }
            nestedToggleButton.style.background = '#f3f4f6';
            nestedToggleButton.style.borderColor = '#d1d5db';
            nestedToggleButton.style.color = '#6b7280';
          });

          nestedToggleButton.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            toggleNestedSections();
          });
        }

        const iconName = allNestedOpen ? 'unfold_less' : 'unfold_more';
        nestedToggleButton.title = allNestedOpen ? 'Collapse nested sections' : 'Expand nested sections';
        nestedToggleButton.innerHTML = `<span class="material-icons" style="font-size: 16px;">${iconName}</span>`;

        if (nestedToggleButton.parentElement !== titleElement) {
          titleElement.appendChild(nestedToggleButton);
        }
            titleElement.style.paddingRight = editor.isEditable ? '5.25rem' : '3rem';
      };

      let lastKnownOpenState = node.attrs['open'];

      // Function to update visibility based on open state
      const updateVisibility = (isOpen: boolean, reason = 'unknown') => {
        // console.log('[CollapsibleSection] updateVisibility called, isOpen:', isOpen, 'reason:', reason);
        dom.setAttribute('data-open', isOpen ? 'true' : 'false');
        
        // Update content visibility - find collapsible-content elements
        const contentElements = dom.querySelectorAll('[data-type="collapsible-content"]');
        
        contentElements.forEach((el) => {
          const contentElement = el as HTMLElement;
          if (isOpen) {
            contentElement.style.maxHeight = 'none';
            contentElement.style.height = 'auto';
            contentElement.style.opacity = '1';
            contentElement.style.padding = '0.5rem 1rem';
            contentElement.style.overflow = 'visible';
            contentElement.style.pointerEvents = 'auto';
          } else {
            contentElement.style.maxHeight = '0';
            contentElement.style.height = '0';
            contentElement.style.opacity = '0';
            contentElement.style.padding = '0';
            contentElement.style.overflow = 'hidden';
            contentElement.style.pointerEvents = 'none';
          }
        });
        
        // Update icon rotation
        const icons = dom.querySelectorAll('.collapsible-icon');
        icons.forEach((el) => {
          const icon = el as HTMLElement;
          icon.style.transform = isOpen ? 'rotate(90deg)' : 'rotate(0deg)';
        });
      };

      // Initial state
      updateVisibility(lastKnownOpenState, 'initial');
      requestAnimationFrame(() => syncNestedToggleButton());

      return {
        dom,
        contentDOM,
        stopEvent: (event: Event) => {
          const target = event.target as HTMLElement | null;
          if (!target) {
            return false;
          }

          if (menuButton && (target === menuButton || menuButton.contains(target))) {
            return true;
          }

          if (nestedToggleButton && (target === nestedToggleButton || nestedToggleButton.contains(target))) {
            return true;
          }

          return false;
        },
        ignoreMutation: (mutation) => {
          if (!(mutation.target instanceof globalThis.Node)) {
            return false;
          }

          return !!(
            (menuButton && menuButton.contains(mutation.target)) ||
            (nestedToggleButton && nestedToggleButton.contains(mutation.target))
          );
        },
        update: (updatedNode: ProseMirrorNode) => {
          if (updatedNode.type.name !== this.name) {
            return false;
          }

          currentNode = updatedNode;
          const updatedId = updatedNode.attrs['collapsibleId'];
          if (updatedId) {
            dom.setAttribute('data-collapsible-id', String(updatedId));
          } else {
            dom.removeAttribute('data-collapsible-id');
          }
          
          // Only update visibility if the open state actually changed
          const newOpenState = updatedNode.attrs['open'];
          // console.log('[CollapsibleSection] update() called, lastKnown:', lastKnownOpenState, 'new:', newOpenState);
          if (newOpenState !== lastKnownOpenState) {
            lastKnownOpenState = newOpenState;
            updateVisibility(newOpenState, 'update');
          }

          syncNestedToggleButton();
          
          return true;
        },
      };
    };
  },

  addCommands() {
    return {
      insertCollapsibleSection:
        () =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: { open: true, collapsibleId: createCollapsibleId() },
            content: [
              {
                type: 'collapsibleTitle',
                content: [{ type: 'text', text: 'Click to expand' }],
              },
              {
                type: 'collapsibleContent',
                content: [{ type: 'paragraph' }],
              },
            ],
          });
        },
      toggleCollapsibleSection:
        () =>
        ({ commands, state }) => {
          const { $from } = state.selection;
          
          // Search for collapsible section in parent nodes
          for (let depth = $from.depth; depth > 0; depth--) {
            const node = $from.node(depth);
            if (node.type.name === this.name) {
              const pos = $from.before(depth);
              return commands.command(({ tr }) => {
                tr.setNodeMarkup(pos, undefined, {
                  ...node.attrs,
                  open: !node.attrs['open'],
                });
                return true;
              });
            }
          }
          
          return false;
        },
      deleteCollapsibleSection:
        () =>
        ({ commands, state }: { commands: any; state: any }) => {
          const { $from } = state.selection;
          
          // Search for collapsible section in parent nodes
          for (let depth = $from.depth; depth > 0; depth--) {
            const node = $from.node(depth);
            if (node.type.name === this.name) {
              const pos = $from.before(depth);
              return commands.command(({ tr }: { tr: any }) => {
                tr.delete(pos, pos + node.nodeSize);
                return true;
              });
            }
          }
          
          return false;
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      // Enter always stays within the Collapsible: in the content it adds a
      // new line (the default ProseMirror Enter chain could otherwise fall
      // back to splitBlock at a depth that escaped the section); in the
      // title - which only allows inline content, so it has no valid
      // sibling to split into - it moves focus down into the content
      // instead of letting the default handling escape the whole section.
      Enter: ({ editor }: { editor: any }) => {
        const { $from, empty } = editor.state.selection;
        if (!empty) {
          return false;
        }

        if ($from.parent.type.name === 'collapsibleTitle') {
          for (let d = $from.depth; d > 0; d--) {
            if ($from.node(d).type.name !== 'collapsibleSection') {
              continue;
            }

            const afterTitle = $from.after($from.depth);
            return editor.commands.command(({ tr, dispatch }: { tr: any; dispatch: any }) => {
              if (dispatch) {
                tr.setSelection(TextSelection.near(tr.doc.resolve(afterTitle + 1), 1));
                dispatch(tr.scrollIntoView());
              }
              return true;
            });
          }
          return false;
        }

        let sawListItem = false;
        for (let d = $from.depth; d > 0; d--) {
          const nodeTypeName = $from.node(d).type.name;

          if (nodeTypeName === 'listItem' || nodeTypeName === 'taskItem') {
            // Inside a list within the section: defer to ListItem/TaskItem's
            // own Enter handling (splitListItem) so bullets/checkboxes
            // continue normally instead of being replaced by a plain
            // splitBlock. Splitting a list item never escapes the section,
            // so there's no need to intercept it here.
            sawListItem = true;
            continue;
          }

          if (nodeTypeName === 'collapsibleContent') {
            if (sawListItem) {
              return false;
            }
            return editor.commands.splitBlock();
          }
        }

        return false;
      },

      'Shift-Enter': ({ editor }: { editor: any }) => exitCollapsibleSection(editor),
      'Mod-Enter': ({ editor }: { editor: any }) => exitCollapsibleSection(editor),

      // Allow backspace to delete entire collapsible section when at start of title
      'Backspace': ({ editor }: { editor: any }) => {
        const { $from, empty } = editor.state.selection;
        
        if (!empty) {
          return false; // Let default handle selection deletion
        }

        // Check if we're at the start of a collapsibleTitle
        if ($from.parent.type.name === 'collapsibleTitle' && $from.parentOffset === 0) {
          // Check if title is empty
          if ($from.parent.textContent.length === 0) {
            // Delete the entire collapsible section
            for (let d = $from.depth; d > 0; d--) {
              const node = $from.node(d);
              if (node.type.name === 'collapsibleSection') {
                const pos = $from.before(d);
                editor.commands.command(({ tr }: { tr: any }) => {
                  tr.delete(pos, pos + node.nodeSize);
                  return true;
                });
                return true;
              }
            }
          }
        }
        
        return false;
      },
      // Allow delete to work similarly
      'Delete': ({ editor }: { editor: any }) => {
        const { $from, empty } = editor.state.selection;
        
        if (!empty) {
          return false;
        }

        // Check if we're at the end of a collapsibleTitle with empty content
        if ($from.parent.type.name === 'collapsibleTitle' && 
            $from.parentOffset === $from.parent.content.size &&
            $from.parent.textContent.length === 0) {
          // Delete the entire collapsible section
          for (let d = $from.depth; d > 0; d--) {
            const node = $from.node(d);
            if (node.type.name === 'collapsibleSection') {
              const pos = $from.before(d);
              editor.commands.command(({ tr }: { tr: any }) => {
                tr.delete(pos, pos + node.nodeSize);
                return true;
              });
              return true;
            }
          }
        }
        
        return false;
      },
      // Ctrl+Shift+C to insert a new collapsible section
      'Mod-Shift-c': ({ editor }: { editor: any }) => {
        const event = new CustomEvent('insertCollapsibleSectionFromSelection', {
          bubbles: true,
          composed: true,
        });
        editor.view.dom.dispatchEvent(event);
        return true;
      },
    };
  },
});

export const CollapsibleTitle = Node.create({
  name: 'collapsibleTitle',

  content: 'inline*',

  defining: true,

  parseHTML() {
    return [
      {
        tag: 'div[data-type="collapsible-title"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(
        {
          'data-type': 'collapsible-title',
          class: 'collapsible-title',
        },
        HTMLAttributes
      ),
      0,
    ];
  },

  addNodeView() {
    return ({ node, getPos, editor }) => {
      const dom = document.createElement('div');
      dom.className = 'collapsible-title';
      dom.setAttribute('data-type', 'collapsible-title');
      // Flex row: [icon wrapper] [editable content]
      dom.style.cssText = 'display: flex; align-items: flex-start; background-color: rgba(59, 130, 246, 0.32); background-color: color-mix(in srgb, var(--primary-color, var(--p-primary-color, #3b82f6)) 28%, white); color: #0f172a; font-weight: 600; position: relative; min-height: 2.25rem;';

      // Icon wrapper – contenteditable="false" tells ProseMirror this is non-content UI
      const iconWrapper = document.createElement('span');
      iconWrapper.contentEditable = 'false';
      iconWrapper.style.cssText = 'flex: 0 0 auto; display: flex; align-items: center; justify-content: center; width: 2rem; min-height: 2.25rem; cursor: pointer; user-select: none; pointer-events: auto;';

      // Chevron icon inside wrapper (pointer-events: none so clicks land on the wrapper)
      const icon = document.createElement('span');
      icon.className = 'material-icons collapsible-icon';
      icon.textContent = 'chevron_right';
      icon.style.cssText = 'display: inline-block; font-size: 1.25rem; transition: transform 200ms; pointer-events: none; user-select: none;';

      iconWrapper.appendChild(icon);

      // Content container – ProseMirror manages children of this element
      const contentDOM = document.createElement('div');
      contentDOM.style.cssText = 'flex: 1; min-width: 0; padding: 0.375rem 0.5rem; min-height: 1.5rem;';
      if (editor.isEditable) {
        contentDOM.style.cursor = 'text';
      } else {
        contentDOM.style.cursor = 'pointer';
        contentDOM.style.userSelect = 'none';
      }

      dom.appendChild(iconWrapper);
      dom.appendChild(contentDOM);

      let lastKnownOpen = false;

      // Update icon rotation based on parent state
      const updateIcon = () => {
        if (typeof getPos === 'function') {
          const pos = getPos();
          if (typeof pos === 'number') {
            try {
              const $pos = editor.state.doc.resolve(pos);
              for (let d = $pos.depth; d >= 0; d--) {
                const parentNode = $pos.node(d);
                if (parentNode.type.name === 'collapsibleSection') {
                  const isOpen = parentNode.attrs['open'];
                  if (lastKnownOpen !== isOpen) {
                    lastKnownOpen = isOpen;
                    icon.style.transform = isOpen ? 'rotate(90deg)' : 'rotate(0deg)';
                  }
                  break;
                }
              }
            } catch (e) {
              // Ignore errors
            }
          }
        }
      };

      // Initial icon state
      updateIcon();

      // Mousedown handler - handle toggle here to prevent ProseMirror interference
      const iconMousedownHandler = (e: MouseEvent) => {
        // console.log('[CollapsibleTitle] Icon mousedown captured, calling toggleSection');
        e.preventDefault(); // Prevents default browser action AND the click event
        e.stopPropagation();
        e.stopImmediatePropagation(); // Prevent any other handlers
        toggleSection(); // Handle toggle directly in mousedown
      };

      // Dom click handler
      const clickHandler = (e: MouseEvent) => {
        const target = e.target as HTMLElement;

        // If clicking on icon wrapper, already handled by mousedown
        if (target === iconWrapper || iconWrapper.contains(target)) {
          return;
        }

        // In edit mode, let ProseMirror handle everything
        if (editor.isEditable) {
          return;
        }

        // In read-only mode, toggle on click
        e.preventDefault();
        e.stopPropagation();
        toggleSection();
      };
      
      const toggleSection = () => {
        // console.log('[CollapsibleTitle] toggleSection called');
        if (typeof getPos === 'function') {
          const pos = getPos();
          if (typeof pos === 'number') {
            try {
              // Find parent collapsibleSection
              const $pos = editor.state.doc.resolve(pos);
              for (let d = $pos.depth; d >= 0; d--) {
                const parentNode = $pos.node(d);
                if (parentNode.type.name === 'collapsibleSection') {
                  const parentPos = d === 0 ? 0 : $pos.before(d);
                  const currentOpenState = parentNode.attrs['open'];
                  const newOpenState = !currentOpenState;
                  
                  // console.log('[CollapsibleTitle] Found parent at pos', parentPos, 'toggling from', currentOpenState, 'to', newOpenState);
                  
                  // Use a transaction with metadata to mark this as a collapsible toggle
                  const tr = editor.state.tr;
                  tr.setMeta('isCollapsibleToggle', true);
                  tr.setMeta('toggleSource', 'CollapsibleTitle icon');
                  tr.setNodeMarkup(parentPos, undefined, {
                    ...parentNode.attrs,
                    open: newOpenState,
                  });
                  
                  // If collapsing, move selection out of the section to prevent typing in hidden content
                  if (!newOpenState) {
                    const sectionEnd = parentPos + parentNode.nodeSize;
                    if (editor.state.selection.from >= parentPos && editor.state.selection.from < sectionEnd) {
                      // Selection is inside the section being collapsed, move it after the section
                      tr.setSelection(TextSelection.near(tr.doc.resolve(sectionEnd)));
                      // console.log('[CollapsibleTitle] Moved selection out of collapsed section');
                    }
                  }
                  
                  editor.view.dispatch(tr);
                  
                  // console.log('[CollapsibleTitle] Transaction dispatched');
                  
                  break;
                }
              }
            } catch (e) {
              console.error('Error in toggleSection:', e);
            }
          }
        }
      };

      // Mousedown on iconWrapper (icon itself has pointer-events: none)
      iconWrapper.addEventListener('mousedown', iconMousedownHandler, { capture: true });
      dom.addEventListener('click', clickHandler);

      return {
        dom,
        contentDOM,
        stopEvent: (event: Event) => {
          // Stop ProseMirror from handling events on the icon wrapper
          const target = event.target as HTMLElement;
          if (target === iconWrapper || iconWrapper.contains(target)) {
            return true;
          }
          return false;
        },
        ignoreMutation: (mutation) => {
          if (!(mutation.target instanceof globalThis.Node)) {
            return false;
          }
          // Only let ProseMirror react to mutations within or directly on contentDOM;
          // ignore icon wrapper, nestedToggleButton, dom attribute/style changes, etc.
          return mutation.target !== contentDOM && !contentDOM.contains(mutation.target);
        },
        update: (updatedNode: ProseMirrorNode) => {
          if (updatedNode.type.name !== 'collapsibleTitle') {
            return false;
          }
          updateIcon();
          return true;
        },
        destroy: () => {
          iconWrapper.removeEventListener('mousedown', iconMousedownHandler, { capture: true });
          dom.removeEventListener('click', clickHandler);
        },
      };
    };
  },
});

export const CollapsibleContent = Node.create({
  name: 'collapsibleContent',

  content: 'block+',

  defining: true,

  parseHTML() {
    return [
      {
        tag: 'div[data-type="collapsible-content"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(
        {
          'data-type': 'collapsible-content',
          class: 'collapsible-content transition-all duration-300 ease-in-out overflow-hidden',
        },
        HTMLAttributes
      ),
      0,
    ];
  },

  addNodeView() {
    return ({ node, getPos, editor }) => {
      // Use the contentDOM directly as the dom element - no wrapper
      const dom = document.createElement('div');
      dom.className = 'collapsible-content';
      dom.setAttribute('data-type', 'collapsible-content');
      dom.style.cssText = 'padding: 0.5rem 1rem; transition: opacity 200ms ease-in-out, padding 200ms ease-in-out; display: block; width: 100%; box-sizing: border-box;';
      
      // Check parent's open state and apply visibility
      const checkParentState = () => {
        if (typeof getPos === 'function') {
          const pos = getPos();
          if (typeof pos === 'number') {
            try {
              const $pos = editor.state.doc.resolve(pos);
              for (let d = $pos.depth; d > 0; d--) {
                const parentNode = $pos.node(d);
                if (parentNode.type.name === 'collapsibleSection') {
                  const isOpen = parentNode.attrs['open'];
                  
                  if (isOpen) {
                    dom.style.maxHeight = 'none';
                    dom.style.height = 'auto';
                    dom.style.opacity = '1';
                    dom.style.overflow = 'visible';
                    dom.style.padding = '0.5rem 1rem';
                    dom.style.pointerEvents = 'auto';
                  } else {
                    dom.style.maxHeight = '0';
                    dom.style.height = '0';
                    dom.style.opacity = '0';
                    dom.style.overflow = 'hidden';
                    dom.style.padding = '0';
                    dom.style.pointerEvents = 'none';
                  }
                  return; // Exit after finding parent
                }
              }
            } catch (e) {
              console.error('Error checking parent state:', e);
            }
          }
        }
      };

      // Initial state
      checkParentState();

      return {
        dom,
        // contentDOM is the same as dom - no extra wrapper
        contentDOM: dom,
        update: (updatedNode: ProseMirrorNode) => {
          if (updatedNode.type.name !== 'collapsibleContent') {
            return false;
          }
          checkParentState();
          return true;
        },
      };
    };
  },
});
